import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Trash2, Plus, Save, X, Calendar, Tag } from 'lucide-react';
import WineSettingsTab from '../components/settings/WineSettingsTab';
import TagManagementTab from '../components/settings/TagManagementTab';

const Settings = () => {
  const { user } = useAuth();
  // State for different utility tables
  const [activeTab, setActiveTab] = useState('varietals');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState({});
  
  // Varietals state
  const [varietals, setVarietals] = useState([]);
  const [newVarietal, setNewVarietal] = useState({ name: '', varShortName: '' });
  
  // Brands state
  const [brands, setBrands] = useState([]);
  const [newBrand, setNewBrand] = useState({ name: '' });
  
  // Fanciful Names state
  const [fancifulNames, setFancifulNames] = useState([]);
  const [newFancifulName, setNewFancifulName] = useState({ name: '' });
  
  // Material Categories state
  const [materialCategories, setMaterialCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  
  // Show/hide forms for adding new items
  const [showVarietalForm, setShowVarietalForm] = useState(false);
  const [showBrandForm, setShowBrandForm] = useState(false);
  const [showFancifulForm, setShowFancifulForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  // Fetch data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        switch (activeTab) {
          case 'varietals':
            // Use the full parameter to get complete varietal records
            const varietalsRes = await axios.get('/api/varietals?full=true');
            setVarietals(varietalsRes.data);
            break;
          case 'brands':
            const brandsRes = await axios.get('/api/brands');
            setBrands(brandsRes.data);
            break;
          case 'fanciful':
            const fancifulRes = await axios.get('/api/fanciful-names');
            setFancifulNames(fancifulRes.data);
            break;
          case 'categories':
            const categoriesRes = await axios.get('/api/material-categories');
            setMaterialCategories(categoriesRes.data);
            break;
          default:
            break;
        }
      } catch (err) {
        console.error(`Error fetching ${activeTab}:`, err);
        setError(`Failed to load ${activeTab}. ${err.response?.data?.message || err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [activeTab]);
  
  // Handle form submission for adding new items
  const handleAddVarietal = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/varietals', newVarietal);
      setVarietals([...varietals, response.data]);
      setNewVarietal({ name: '', varShortName: '' });
      setShowVarietalForm(false);
    } catch (err) {
      setError(`Failed to add varietal: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const handleAddBrand = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/brands', newBrand);
      setBrands([...brands, response.data]);
      setNewBrand({ name: '' });
      setShowBrandForm(false);
    } catch (err) {
      setError(`Failed to add brand: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const handleAddFancifulName = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/fanciful-names', newFancifulName);
      setFancifulNames([...fancifulNames, response.data]);
      setNewFancifulName({ name: '' });
      setShowFancifulForm(false);
    } catch (err) {
      setError(`Failed to add fanciful name: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/material-categories', newCategory);
      setMaterialCategories([...materialCategories, response.data]);
      setNewCategory({ name: '', description: '' });
      setShowCategoryForm(false);
    } catch (err) {
      setError(`Failed to add category: ${err.response?.data?.message || err.message}`);
    }
  };
  
  // Handle delete functionality
  // Handle inline editing functions
  const startEditing = (item, type) => {
    setEditingItem({ id: item.id || item.categoryID, type });
    
    // Initialize edit value based on item type
    switch(type) {
      case 'varietal':
        setEditValue({ name: item.name, varShortName: item.varShortName || '' });
        break;
      case 'brand':
      case 'fanciful':
        setEditValue({ name: item.name });
        break;
      case 'category':
        setEditValue({ name: item.name, description: item.description || '' });
        break;
      default:
        setEditValue({});
    }
  };
  
  const cancelEditing = () => {
    setEditingItem(null);
    setEditValue({});
  };
  
  const updateVarietal = async () => {
    try {
      const response = await axios.put(`/api/varietals/${editingItem.id}`, editValue);
      setVarietals(varietals.map(v => v.id === editingItem.id ? {...v, ...editValue} : v));
      cancelEditing();
    } catch (err) {
      setError(`Failed to update varietal: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const updateBrand = async () => {
    try {
      const response = await axios.put(`/api/brands/${editingItem.id}`, editValue);
      setBrands(brands.map(b => b.id === editingItem.id ? {...b, ...editValue} : b));
      cancelEditing();
    } catch (err) {
      setError(`Failed to update brand: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const updateFancifulName = async () => {
    try {
      const response = await axios.put(`/api/fanciful-names/${editingItem.id}`, editValue);
      setFancifulNames(fancifulNames.map(f => f.id === editingItem.id ? {...f, ...editValue} : f));
      cancelEditing();
    } catch (err) {
      setError(`Failed to update fanciful name: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const updateCategory = async () => {
    try {
      const response = await axios.put(`/api/material-categories/${editingItem.id}`, editValue);
      setMaterialCategories(materialCategories.map(c => 
        c.categoryID === editingItem.id ? {...c, ...editValue} : c
      ));
      cancelEditing();
    } catch (err) {
      setError(`Failed to update category: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const saveEdit = async () => {
    if (!editingItem) return;
    
    switch(editingItem.type) {
      case 'varietal':
        await updateVarietal();
        break;
      case 'brand':
        await updateBrand();
        break;
      case 'fanciful':
        await updateFancifulName();
        break;
      case 'category':
        await updateCategory();
        break;
      default:
        cancelEditing();
    }
  };
  
  // Handle key press in edit mode
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleDeleteVarietal = async (id) => {
    if (!window.confirm('Are you sure you want to delete this varietal?')) return;
    
    try {
      await axios.delete(`/api/varietals/${id}`);
      setVarietals(varietals.filter(v => v.id !== id));
    } catch (err) {
      setError(`Failed to delete varietal: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const handleDeleteBrand = async (id) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) return;
    
    try {
      await axios.delete(`/api/brands/${id}`);
      setBrands(brands.filter(b => b.id !== id));
    } catch (err) {
      setError(`Failed to delete brand: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const handleDeleteFancifulName = async (id) => {
    if (!window.confirm('Are you sure you want to delete this fanciful name?')) return;
    
    try {
      await axios.delete(`/api/fanciful-names/${id}`);
      setFancifulNames(fancifulNames.filter(f => f.id !== id));
    } catch (err) {
      setError(`Failed to delete fanciful name: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this material category?')) return;
    
    try {
      await axios.delete(`/api/material-categories/${id}`);
      setMaterialCategories(materialCategories.filter(c => c.categoryID !== id));
    } catch (err) {
      setError(`Failed to delete category: ${err.response?.data?.message || err.message}`);
    }
  };
  
  return (
    <div className="container mt-4">
      <div className="card">
        <div className="card-header">
          <h4 className="mb-0">System Settings</h4>
        </div>
        <div className="card-body">
          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'varietals' ? 'active' : ''}`}
                onClick={() => setActiveTab('varietals')}
              >
                Varietals
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'brands' ? 'active' : ''}`}
                onClick={() => setActiveTab('brands')}
              >
                Brands
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'fanciful' ? 'active' : ''}`}
                onClick={() => setActiveTab('fanciful')}
              >
                Fanciful Names
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'categories' ? 'active' : ''}`}
                onClick={() => setActiveTab('categories')}
              >
                Material Categories
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'wine-settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('wine-settings')}
              >
                <Calendar size={16} className="me-1" />
                Topping Schedule
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'tags' ? 'active' : ''}`}
                onClick={() => setActiveTab('tags')}
              >
                <Tag size={16} className="me-1" />
                Tag Management
              </button>
            </li>
          </ul>
          
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>
          )}
          
          {loading ? (
            <div className="d-flex justify-content-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="tab-content">
              {/* Varietals Tab */}
              {activeTab === 'varietals' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Varietals</h5>
                    <button 
                      className="btn btn-sm btn-outline-primary" 
                      onClick={() => setShowVarietalForm(true)}
                    >
                      <Plus size={16} className="me-1" />
                      Add New Varietal
                    </button>
                  </div>
                  
                  {showVarietalForm && (
                    <div className="card mb-3">
                      <div className="card-body">
                        <form onSubmit={handleAddVarietal}>
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <label className="form-label">Varietal Name</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                value={newVarietal.name}
                                onChange={(e) => setNewVarietal({...newVarietal, name: e.target.value})}
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Short Name (4 letters max)</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                value={newVarietal.varShortName}
                                onChange={(e) => setNewVarietal({...newVarietal, varShortName: e.target.value.slice(0, 4)})}
                                maxLength={4}
                              />
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-outline-success">
                              <Save size={16} className="me-1" />
                              Save
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-outline-secondary"
                              onClick={() => setShowVarietalForm(false)}
                            >
                              <X size={16} className="me-1" />
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}
                  
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Short Name</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {varietals.map((varietal) => (
                          <tr key={varietal.id}>
                            <td>{varietal.id}</td>
                            <td onClick={() => startEditing(varietal, 'varietal')} style={{ cursor: 'pointer' }}>
                              {editingItem && editingItem.id === varietal.id && editingItem.type === 'varietal' ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editValue.name}
                                  onChange={(e) => setEditValue({...editValue, name: e.target.value})}
                                  onKeyDown={handleKeyPress}
                                  autoFocus
                                />
                              ) : (
                                <span className="editable-cell">{varietal.name}</span>
                              )}
                            </td>
                            <td onClick={() => startEditing(varietal, 'varietal')} style={{ cursor: 'pointer' }}>
                              {editingItem && editingItem.id === varietal.id && editingItem.type === 'varietal' ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editValue.varShortName}
                                  onChange={(e) => setEditValue({...editValue, varShortName: e.target.value.slice(0, 4)})}
                                  onKeyDown={handleKeyPress}
                                  maxLength={4}
                                />
                              ) : (
                                <span className="editable-cell">{varietal.varShortName}</span>
                              )}
                            </td>
                            <td>
                              {editingItem && editingItem.id === varietal.id ? (
                                <div className="btn-group btn-group-sm">
                                  <button 
                                    className="btn btn-sm btn-outline-success"
                                    onClick={saveEdit}
                                    title="Save"
                                  >
                                    <Save size={16} />
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={cancelEditing}
                                    title="Cancel"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <button 
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteVarietal(varietal.id)}
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Brands Tab */}
              {activeTab === 'brands' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Brands</h5>
                    <button 
                      className="btn btn-sm btn-outline-primary" 
                      onClick={() => setShowBrandForm(true)}
                    >
                      <Plus size={16} className="me-1" />
                      Add New Brand
                    </button>
                  </div>
                  
                  {showBrandForm && (
                    <div className="card mb-3">
                      <div className="card-body">
                        <form onSubmit={handleAddBrand}>
                          <div className="mb-3">
                            <label className="form-label">Brand Name</label>
                            <input 
                              type="text" 
                              className="form-control" 
                              value={newBrand.name}
                              onChange={(e) => setNewBrand({...newBrand, name: e.target.value})}
                              required
                            />
                          </div>
                          <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-outline-success">
                              <Save size={16} className="me-1" />
                              Save
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-outline-secondary"
                              onClick={() => setShowBrandForm(false)}
                            >
                              <X size={16} className="me-1" />
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}
                  
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brands.map((brand) => (
                          <tr key={brand.id}>
                            <td>{brand.id}</td>
                            <td onClick={() => startEditing(brand, 'brand')} style={{ cursor: 'pointer' }}>
                              {editingItem && editingItem.id === brand.id && editingItem.type === 'brand' ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editValue.name}
                                  onChange={(e) => setEditValue({...editValue, name: e.target.value})}
                                  onKeyDown={handleKeyPress}
                                  autoFocus
                                />
                              ) : (
                                <span className="editable-cell">{brand.name}</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${brand.active ? 'bg-success' : 'bg-danger'}`}>
                                {brand.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              {editingItem && editingItem.id === brand.id ? (
                                <div className="btn-group btn-group-sm">
                                  <button 
                                    className="btn btn-sm btn-outline-success"
                                    onClick={saveEdit}
                                    title="Save"
                                  >
                                    <Save size={16} />
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={cancelEditing}
                                    title="Cancel"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <button 
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteBrand(brand.id)}
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Fanciful Names Tab */}
              {activeTab === 'fanciful' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Fanciful Names</h5>
                    <button 
                      className="btn btn-sm btn-outline-primary" 
                      onClick={() => setShowFancifulForm(true)}
                    >
                      <Plus size={16} className="me-1" />
                      Add New Fanciful Name
                    </button>
                  </div>
                  
                  {showFancifulForm && (
                    <div className="card mb-3">
                      <div className="card-body">
                        <form onSubmit={handleAddFancifulName}>
                          <div className="mb-3">
                            <label className="form-label">Fanciful Name</label>
                            <input 
                              type="text" 
                              className="form-control" 
                              value={newFancifulName.name}
                              onChange={(e) => setNewFancifulName({...newFancifulName, name: e.target.value})}
                              required
                            />
                          </div>
                          <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-outline-success">
                              <Save size={16} className="me-1" />
                              Save
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-outline-secondary"
                              onClick={() => setShowFancifulForm(false)}
                            >
                              <X size={16} className="me-1" />
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}
                  
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fancifulNames.map((name) => (
                          <tr key={name.id}>
                            <td>{name.id}</td>
                            <td onClick={() => startEditing(name, 'fanciful')} style={{ cursor: 'pointer' }}>
                              {editingItem && editingItem.id === name.id && editingItem.type === 'fanciful' ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editValue.name}
                                  onChange={(e) => setEditValue({...editValue, name: e.target.value})}
                                  onKeyDown={handleKeyPress}
                                  autoFocus
                                />
                              ) : (
                                <span className="editable-cell">{name.name}</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${name.active ? 'bg-success' : 'bg-danger'}`}>
                                {name.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              {editingItem && editingItem.id === name.id ? (
                                <div className="btn-group btn-group-sm">
                                  <button 
                                    className="btn btn-sm btn-outline-success"
                                    onClick={saveEdit}
                                    title="Save"
                                  >
                                    <Save size={16} />
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={cancelEditing}
                                    title="Cancel"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <button 
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteFancifulName(name.id)}
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Material Categories Tab */}
              {activeTab === 'categories' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Material Categories</h5>
                    <button 
                      className="btn btn-sm btn-outline-primary" 
                      onClick={() => setShowCategoryForm(true)}
                    >
                      <Plus size={16} className="me-1" />
                      Add New Category
                    </button>
                  </div>
                  
                  {showCategoryForm && (
                    <div className="card mb-3">
                      <div className="card-body">
                        <form onSubmit={handleAddCategory}>
                          <div className="mb-3">
                            <label className="form-label">Category Name</label>
                            <input 
                              type="text" 
                              className="form-control" 
                              value={newCategory.name}
                              onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Description</label>
                            <textarea 
                              className="form-control" 
                              value={newCategory.description}
                              onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                            />
                          </div>
                          <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-outline-success">
                              <Save size={16} className="me-1" />
                              Save
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-outline-secondary"
                              onClick={() => setShowCategoryForm(false)}
                            >
                              <X size={16} className="me-1" />
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}
                  
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {materialCategories.map((category) => (
                          <tr key={category.categoryID}>
                            <td>{category.categoryID}</td>
                            <td onClick={() => startEditing(category, 'category')} style={{ cursor: 'pointer' }}>
                              {editingItem && editingItem.id === category.categoryID && editingItem.type === 'category' ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editValue.name}
                                  onChange={(e) => setEditValue({...editValue, name: e.target.value})}
                                  onKeyDown={handleKeyPress}
                                  autoFocus
                                />
                              ) : (
                                <span className="editable-cell">{category.name}</span>
                              )}
                            </td>
                            <td onClick={() => startEditing(category, 'category')} style={{ cursor: 'pointer' }}>
                              {editingItem && editingItem.id === category.categoryID && editingItem.type === 'category' ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editValue.description || ''}
                                  onChange={(e) => setEditValue({...editValue, description: e.target.value})}
                                  onKeyDown={handleKeyPress}
                                />
                              ) : (
                                <span className="editable-cell">{category.description}</span>
                              )}
                            </td>
                            <td>
                              {editingItem && editingItem.id === category.categoryID ? (
                                <div className="btn-group btn-group-sm">
                                  <button 
                                    className="btn btn-sm btn-outline-success"
                                    onClick={saveEdit}
                                    title="Save"
                                  >
                                    <Save size={16} />
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={cancelEditing}
                                    title="Cancel"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <button 
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteCategory(category.categoryID)}
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Wine Settings Tab */}
              {activeTab === 'wine-settings' && <WineSettingsTab />}

              {/* Tag Management Tab */}
              {activeTab === 'tags' && <TagManagementTab />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;